class KetoBenefitsSlider {
  constructor(sliderElement) {
    this.slider = sliderElement;
    this.track = sliderElement?.querySelector('.keto-benefits__track');
    this.cards = Array.from(sliderElement?.querySelectorAll('.keto-benefits__card') || []);
    this.prevBtn = sliderElement?.querySelector('.keto-benefits__arrow--prev');
    this.nextBtn = sliderElement?.querySelector('.keto-benefits__arrow--next');

    if (this.cards.length === 0) return;

    this.isNudging = false;
    this.positionX = 0;
    this.isMobile = window.innerWidth <= 768;
    this.rafId = 0;
    this.nudgeRafId = 0;
    this.lastTs = 0;
    // Constant speed (px/s). Chosen to feel slow + uniform.
    this.speedPxPerSecond = 28;

    // Order matters: updateDimensions ustawia this.gap zanim cloneCardsForLoop
    // policzy setWidth — inaczej na mobile clone count by liczył się dla
    // desktop'owego gap (20px) zamiast mobile (12px).
    this.updateDimensions();
    this.cloneCardsForLoop();
    this.init();
  }

  cloneCardsForLoop() {
    const original = this.cards;
    const originalCount = original.length;
    if (!this.track || originalCount === 0) return;

    this.track.querySelectorAll('.keto-benefits__card--clone').forEach((n) => n.remove());

    const setWidth = this.getCardsSetWidth(original);
    const viewportWidth = this.slider.getBoundingClientRect().width;
    // Need enough copies that the visible window is always covered after the wrap.
    // (translate is in (-setWidth, 0], so the rightmost card position can be setWidth + viewport.)
    const minCopies = 2;
    const copies = Math.max(minCopies, Math.ceil((viewportWidth + setWidth) / Math.max(1, setWidth)));

    for (let c = 0; c < copies; c++) {
      for (let i = 0; i < originalCount; i++) {
        const clone = original[i].cloneNode(true);
        clone.classList.add('keto-benefits__card--clone');
        this.track.appendChild(clone);
      }
    }

    this.allCards = Array.from(this.track.querySelectorAll('.keto-benefits__card'));
    this.originalCardsCount = originalCount;
    this.setWidth = setWidth;
  }

  updateDimensions() {
    if (this.cards.length === 0) return;

    const firstCard = this.cards[0];
    this.cardWidth = firstCard.offsetWidth || firstCard.getBoundingClientRect().width;
    this.gap = this.isMobile ? 12 : 20;
    this.slideWidth = this.cardWidth + this.gap;
    this.setWidth = this.getCardsSetWidth(this.cards);
  }

  getCardsSetWidth(cards) {
    if (!cards?.length) return 0;
    const gap = this.gap ?? 20;
    const widths = cards.map((c) => c.getBoundingClientRect().width);
    const total = widths.reduce((a, b) => a + b, 0);
    // Include trailing gap (between last card of set N and first card of set N+1)
    // — track ma `display: flex; gap`, więc każdy clone-set jest oddzielony
    // gap'em od poprzedniego. Bez tej kompensacji wrap o setWidth zostawia
    // szczeline (gap) i wygląda jak nagły jerk.
    return total + gap * cards.length;
  }

  init() {
    // Kafelki to luźne hasła, nie linki — brak drag/swipe i pauzy na hover,
    // żeby nic nie sugerowało, że da się w nie kliknąć albo je przesunąć.
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.nudgeBySlides(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nudgeBySlides(1));
    }

    this.positionX = 0;
    this.applyTransform();
    // Pętla klatek tylko wtedy, gdy pasek jest widoczny. Bez tego chodziła od wejścia
    // na stronę do wyjścia i zabierała wątek główny podczas ładowania (pomiar 14.10).
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.start();
      else this.stop();
    }).observe(this.slider);

    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.isMobile = window.innerWidth <= 768;
        this.updateDimensions();
        this.cloneCardsForLoop();
        this.wrapPosition();
        this.applyTransform();
      }, 250);
    });
  }

  start() {
    this.stop();
    this.lastTs = 0;
    this.rafId = requestAnimationFrame((ts) => this.tick(ts));
  }

  stop() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = 0;
  }

  tick(ts) {
    if (!this.lastTs) this.lastTs = ts;
    const dt = Math.min(64, ts - this.lastTs);
    this.lastTs = ts;

    if (!this.isNudging) {
      const delta = (this.speedPxPerSecond * dt) / 1000;
      this.positionX -= delta;
      this.wrapPosition();
      this.applyTransform();
    }

    this.rafId = requestAnimationFrame((t) => this.tick(t));
  }

  // Keep positionX in (-setWidth, 0]. Using the math modulo identity:
  // ((x % w) + w) % w gives a result in [0, w); subtract w when > 0.
  wrapPosition() {
    const setWidth = this.setWidth || 0;
    if (setWidth <= 0) return;
    let p = ((this.positionX % setWidth) + setWidth) % setWidth;
    if (p > 0) p -= setWidth;
    this.positionX = p;
  }

  applyTransform() {
    this.track.style.transform = `translate3d(${this.positionX}px, 0, 0)`;
  }

  // Nudge by N slide widths. Accumulates a delta and animates it onto positionX,
  // wrapping each frame so the rendered translate is always in the valid range.
  // Subsequent clicks cancel the previous animation and start a fresh one from
  // the current position — clicking faster than the duration interrupts but the
  // effective movement always proceeds from "where we are now".
  nudgeBySlides(dir) {
    if (!this.slideWidth) return;

    if (this.nudgeRafId) {
      cancelAnimationFrame(this.nudgeRafId);
      this.nudgeRafId = 0;
    }

    const totalDelta = -this.slideWidth * dir;
    const duration = 240;
    const start = performance.now();
    let lastApplied = 0;

    this.isNudging = true;
    const animate = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const ease = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      const applied = totalDelta * ease;
      const step = applied - lastApplied;
      lastApplied = applied;

      this.positionX += step;
      this.wrapPosition();
      this.applyTransform();

      if (t < 1) {
        this.nudgeRafId = requestAnimationFrame(animate);
      } else {
        this.nudgeRafId = 0;
        this.isNudging = false;
      }
    };
    this.nudgeRafId = requestAnimationFrame(animate);
  }

}

document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('#keto-benefits-slider');
  sliders.forEach((slider) => {
    if (!slider.dataset.initialized) {
      slider.dataset.initialized = 'true';
      new KetoBenefitsSlider(slider);
    }
  });
});

if (typeof document.addEventListener !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          const newSlider = node.querySelector?.('#keto-benefits-slider') ||
                           (node.id === 'keto-benefits-slider' ? node : null);
          if (newSlider && !newSlider.dataset.initialized) {
            newSlider.dataset.initialized = 'true';
            new KetoBenefitsSlider(newSlider);
          }
        }
      });
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}
